import { getRoutePathList, getUrlPathString } from './CanonicalHelper';

// Faithful port of AnalyticsHelper.res getCategoryName — the full route table.
// The category feeds the city context and every GA click event, so each branch
// (including fall-through-to-Default gaps like `/referral-tnc` vs the real
// `/referral_tnc` route) is preserved exactly as the .res behaves in prod.
const DEFAULT = 'Default';
const PARTNERS = 'Partners';
const SERVICES_PILLAR = 'Service Page';
const SERVICES_KEYWORD = 'Service Page - ';
const SERVICES_TRUCKS_KEYWORD = 'Trucks Keyword - ';
const SERVICES_2W_KEYWORD = '2W Keyword - ';
const SERVICES_COURIER_KEYWORD = 'Courier Keyword - ';
const V2_2W_CITY = 'Two Wheelers Page - ';
const TRUCKS_CITY = 'City ';
const TRUCKS_INVENTORY = 'City Inventory - ';
const FARE_ESTIMATE_MOB = 'Fare Estimate Result';
const ENTERPRISE = 'Enterprise';
const CITY = 'City ';
const SUPPORT = 'Support';
const PRIVACY_POLICY = 'Privacy Policy';
const PRIVACY_POLICY_V1 = 'Privacy Policy V1';
const PRIVACY_POLICY_V2 = 'Privacy Policy V2';
const ENTERPRISE_FAQ = 'Enterprise Faq';
const SPOT_FAQ = 'Spot Faq';
const PORTER_WALLET_FAQ = 'Porter Wallet Faq';
const INSURANCE_FAQ = 'Insurance Faq';
const TERMS_OF_SERVICE_PARTNERS = 'ToS Partners';
const REFERRAL_TNC = 'Refferal TnC';
const COOKIE_POLICY = 'Cookie Policy';
const COURIER_TNC = 'Courier TnC';
const TERMS_OF_SERVICE = 'Terms of Service';
const ABOUT_US = 'About Us';
const CAREERS = 'Careers';
const OFFERS = 'Offers';
const OFFERS_CASHBACK = 'Cashback';
const OFFERS_DISCOUNT = 'Discount';
const COURIER = 'Courier';
const COURIER_CITY = 'Courier Page - ';
const GOVERNANCE = 'Governance';
const HOME = 'Home';
const TRUCKS_PILLAR = 'Trucks Main';
const BIKES_PILLAR = 'Bikes Main';

const GA_PATH_BIKE_PARCEL_KEYWORD_PREFIX = '/bike-parcel-delivery-';
const GA_PATH_TRUCKS_PREFIX = '/rent-mini-trucks-in-';
const GA_PATH_TRUCKS_INVENTORY_PREFIX = '/rent-mini-trucks-in-';
const GA_PATH_CITY_PAGE_PREFIX = '/rent-mini-trucks-in-';

const SINGLE_SEGMENT_CATEGORIES: Record<string, string> = {
  'partners': PARTNERS,
  'services': SERVICES_PILLAR,
  'bikes': BIKES_PILLAR,
  'trucks': TRUCKS_PILLAR,
  'fare_estimate_mob': FARE_ESTIMATE_MOB,
  'enterprise': ENTERPRISE,
  'support': SUPPORT,
  'privacy-policy': PRIVACY_POLICY,
  'privacy-policy-v1': PRIVACY_POLICY_V1,
  'privacy-policy-v2': PRIVACY_POLICY_V2,
  'enterprise-faq': ENTERPRISE_FAQ,
  'spot-faq': SPOT_FAQ,
  'porter-wallet-faq': PORTER_WALLET_FAQ,
  'insurance-faq': INSURANCE_FAQ,
  'referral-tnc': REFERRAL_TNC,
  'cookie-policy': COOKIE_POLICY,
  'terms-of-service': TERMS_OF_SERVICE,
  'about-us': ABOUT_US,
  'careers': CAREERS,
  'offers': OFFERS,
  'courier': COURIER,
  'governance': GOVERNANCE,
  '': HOME,
};

function asPathSegment(asPath: string, index: number): string {
  return getRoutePathList(asPath)[index] ?? '';
}

function sliceAsPathFrom(asPath: string, prefix: string): string {
  return getUrlPathString(asPath).slice(prefix.length);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

interface RouterLocation {
  pathname?: string | null;
  asPath?: string | null;
}

// The category feeds useMainLayout's city context on every page render, so
// this must never throw — malformed router fields degrade to Default instead.
export function getCategoryName(
  router: RouterLocation | null | undefined,
): string {
  try {
    return categoryFromLocation(
      asString(router?.pathname),
      asString(router?.asPath),
    );
  } catch {
    return DEFAULT;
  }
}

type SegmentResolver = (asPath: string, second: string) => string;

const TWO_SEGMENT_RESOLVERS: Record<string, SegmentResolver> = {
  'services': asPath => SERVICES_KEYWORD + asPathSegment(asPath, 1),
  'bikes': asPath =>
    V2_2W_CITY + sliceAsPathFrom(asPath, GA_PATH_BIKE_PARCEL_KEYWORD_PREFIX),
  'trucks': asPath =>
    TRUCKS_CITY + sliceAsPathFrom(asPath, GA_PATH_TRUCKS_PREFIX),
  'city': asPath => CITY + sliceAsPathFrom(asPath, GA_PATH_CITY_PAGE_PREFIX),
  'terms-of-service': (_asPath, second) =>
    second === 'partners' ? TERMS_OF_SERVICE_PARTNERS : DEFAULT,
  'courier': (asPath, second) =>
    second === 'tnc' ? COURIER_TNC : COURIER_CITY + asPathSegment(asPath, 1),
};

const SERVICES_KEYWORD_PREFIXES: Record<string, string> = {
  'trucks': SERVICES_TRUCKS_KEYWORD,
  'two-wheelers': SERVICES_2W_KEYWORD,
  'courier': SERVICES_COURIER_KEYWORD,
};

const OFFERS_CATEGORIES: Record<string, string> = {
  cashback: OFFERS_CASHBACK,
  discount: OFFERS_DISCOUNT,
};

function threeSegmentCategory(
  first: string,
  second: string,
  asPath: string,
): string {
  if (first === 'services') {
    const prefix = SERVICES_KEYWORD_PREFIXES[second];
    return prefix === undefined ? DEFAULT : prefix + asPathSegment(asPath, 2);
  }
  if (first === 'trucks') {
    return (
      TRUCKS_INVENTORY +
      sliceAsPathFrom(asPath, GA_PATH_TRUCKS_INVENTORY_PREFIX) +
      ' ' +
      asPathSegment(asPath, 2)
    );
  }
  if (first === 'offers') {
    return OFFERS_CATEGORIES[second] ?? DEFAULT;
  }
  return DEFAULT;
}

function categoryFromLocation(pathname: string, asPath: string): string {
  const segments = pathname.split('/').slice(1);
  const [first = '', second = ''] = segments;

  switch (segments.length) {
    case 1:
      return SINGLE_SEGMENT_CATEGORIES[first] ?? DEFAULT;
    case 2:
      return TWO_SEGMENT_RESOLVERS[first]?.(asPath, second) ?? DEFAULT;
    case 3:
      return threeSegmentCategory(first, second, asPath);
    default:
      return DEFAULT;
  }
}
